import { useMemo, useState } from "react";

import { Search } from "lucide-react";
import { parseAsString, useQueryState } from "nuqs";

import { Filter } from "@/components/ui/filter";
import { Input } from "@/components/ui/input";
import {
  ScenarioAssignmentDisplayStatus,
  usePaginatedRoleplaysQuery,
  useScenarioAssignmentFilterOptionsQuery,
  useScenariosListQuery,
} from "@/graphql";

interface ScenarioAssignmentFiltersProps {
  workspaceId: string;
  pageType: "scenarioDetail" | "roleplayDetail";

  // Filter state (passed from parent)
  selectedCollections: string[];
  selectedScenarios: string[];
  selectedAssigners: string[];
  selectedAssignees: string[];
  selectedStatuses: string[];

  // Filter handlers
  onCollectionsChange: (values: string[]) => void;
  onScenariosChange: (values: string[]) => void;
  onAssignersChange: (values: string[]) => void;
  onAssigneesChange: (values: string[]) => void;
  onStatusesChange: (values: string[]) => void;
}

export function ScenarioAssignmentFilters({
  workspaceId,
  pageType,
  selectedCollections,
  selectedScenarios,
  selectedAssigners,
  selectedAssignees,
  selectedStatuses,
  onCollectionsChange,
  onScenariosChange,
  onAssignersChange,
  onAssigneesChange,
  onStatusesChange,
}: ScenarioAssignmentFiltersProps) {
  // Internal query states for search functionality

  const [globalSearch, setGlobalSearch] = useQueryState(
    "search",
    parseAsString.withDefault(""),
  );
  const [assigneeQuery, setAssigneeQuery] = useState("");
  const [assignerQuery, setAssignerQuery] = useState("");
  const [collectionQuery, setCollectionQuery] = useState("");
  const [scenarioQuery, setScenarioQuery] = useState("");
  // Fetch filter options for assigners and assignees
  const { data: filterOptionsData } = useScenarioAssignmentFilterOptionsQuery({
    variables: {
      workspaceId,
    },
    skip: !workspaceId,
  });

  // Fetch collections (roleplays) with query support
  const { data: roleplaysData } = usePaginatedRoleplaysQuery({
    variables: {
      currentPage: 1,
      pageSize: 100,
      ...(collectionQuery && { query: collectionQuery }),
    },
    skip: !workspaceId || pageType !== "roleplayDetail",
  });

  // Fetch scenarios with query support
  const { data: scenariosData } = useScenariosListQuery({
    variables: {
      page: 1,
      pageSize: 10,
      ...(scenarioQuery && { query: scenarioQuery }),
    },
    skip: !workspaceId || pageType !== "roleplayDetail",
  });

  // Prepare filter options
  const collectionOptions = useMemo(() => {
    if (!roleplaysData?.paginatedRoleplays?.data) return [];
    return roleplaysData.paginatedRoleplays.data.map((r) => ({
      value: r?.id || "",
      label: r?.name || "",
    }));
  }, [roleplaysData]);

  const scenarioOptions = useMemo(() => {
    if (!scenariosData?.scenarios?.data) return [];
    return scenariosData.scenarios.data.map((s) => ({
      value: s?.id || "",
      label: s?.name || "",
    }));
  }, [scenariosData]);

  const assignerOptions = useMemo(() => {
    if (!filterOptionsData?.scenarioAssignmentFilterOptions?.assigners)
      return [];
    return filterOptionsData.scenarioAssignmentFilterOptions.assigners.map(
      (a) => ({
        value: a?.id || "",
        label: a?.name || a?.email || "",
      }),
    );
  }, [filterOptionsData, assigneeQuery]);

  const assigneeOptions = useMemo(() => {
    if (!filterOptionsData?.scenarioAssignmentFilterOptions?.assignees)
      return [];
    return filterOptionsData.scenarioAssignmentFilterOptions.assignees.map(
      (a) => ({
        value: a?.id || "",
        label: a?.name || a?.email || "",
      }),
    );
  }, [filterOptionsData, assigneeQuery]);

  const statusOptions = [
    {
      label: "Not Started",
      value: ScenarioAssignmentDisplayStatus.NotStarted as string,
    },
    {
      label: "In Progress",
      value: ScenarioAssignmentDisplayStatus.InProgress as string,
    },
    {
      label: "Past Due",
      value: ScenarioAssignmentDisplayStatus.PastDue as string,
    },
    {
      label: "Completed",
      value: ScenarioAssignmentDisplayStatus.Completed as string,
    },
    {
      label: "Did Not Pass",
      value: ScenarioAssignmentDisplayStatus.DidNotPass as string,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Global Search */}
      <div className="flex items-center border border-gray-300 rounded-lg px-2 h-10 w-full md:w-1/2">
        <Search className="h-4" />
        <Input
          placeholder="Search assignments by name, email, or scenario"
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          className="max-w-s border-none shadow-none px-1"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="flex flex-wrap gap-2">
          {pageType === "roleplayDetail" && (
            <>
              <Filter<string>
                label="Collection"
                options={collectionOptions}
                onChange={(values: string[]) => onCollectionsChange(values)}
                onSearchChange={setCollectionQuery}
                showSearch
                variant="multiple"
                values={selectedCollections}
              />
              <Filter<string>
                label="Scenario"
                options={scenarioOptions}
                onChange={(values: string[]) => onScenariosChange(values)}
                onSearchChange={setScenarioQuery}
                showSearch
                variant="multiple"
                values={selectedScenarios}
              />
            </>
          )}
          {pageType === "scenarioDetail" && (
            <Filter<string>
              label="Assigned By"
              options={assignerOptions.filter((a) =>
                a.label.toLowerCase().includes(assignerQuery.toLowerCase()),
              )}
              onChange={(values: string[]) => onAssignersChange(values)}
              showSearch
              variant="multiple"
              values={selectedAssigners}
              onSearchChange={(query) => setAssignerQuery(query)}
            />
          )}
          <Filter<string>
            label="Assignees"
            options={assigneeOptions.filter((a) =>
              a.label.toLowerCase().includes(assigneeQuery.toLowerCase()),
            )}
            onChange={(values: string[]) => onAssigneesChange(values)}
            showSearch
            variant="multiple"
            values={selectedAssignees}
            onSearchChange={(query) => setAssigneeQuery(query)}
          />
          <Filter<string>
            label="Status"
            variant="multiple"
            options={statusOptions}
            onChange={(values: string[]) => onStatusesChange(values)}
            values={selectedStatuses}
          />
        </div>
      </div>
    </div>
  );
}
